import GenericQueue from '../../../GenericQueue'
import { CIMSTags } from 'components/CIMSTags'
import { NetworkStatus } from '@apollo/client'
import { JobStatus, QueuableRequestStatus, Report } from '@cims-online/shared'
import { Space } from 'antd'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import {
  useQueuedBatchReportJobsQuery,
  useCancelBatchReportJobMutation,
} from 'schema'
import {
  DefaultQueryHookOptions,
  DefaultMutationHookOptions,
} from 'utils/defaults'
import { dateFormatter } from 'utils/dateFormatter'
import { useProjectContext } from 'context/ProjectContext'
import { useAuthContext } from 'context/AuthContext'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import {
  BatchReportQueueRequest,
  toQueuedBatchReportJob,
} from './utils/toQueuedBatchReportJob'
import BatchReportJobStatus from './components/BatchReportJobStatus'
import { useQueuePolling } from './hooks/useQueuePolling'

const Queue: React.FC = () => {
  const { projectId } = useProjectContext()
  const { user } = useAuthContext()
  const { payItems } = usePayItemSelectContext()
  const { Generating, Queued } = QueuableRequestStatus
  const { data, refetch, loading, networkStatus, startPolling, stopPolling } =
    useQueuedBatchReportJobsQuery({
      ...DefaultQueryHookOptions,
      variables: {
        input: {
          orderBy: [{ field: 'createdDate' }],
          projectId: { equals: projectId },
          statusId: { list: [JobStatus.Pending.id, JobStatus.Processing.id] },
        },
      },
    })
  const [cancel] = useCancelBatchReportJobMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () =>
      simpleSuccessNotification('Batch Report Request successfully canceled!'),
  })
  const requests = (data?.listBatchReportJobs.records ?? []).map((job) =>
    toQueuedBatchReportJob(job, user, payItems)
  )
  const hasRequests = requests.length > 0
  useQueuePolling(hasRequests, startPolling, stopPolling)
  return (
    <GenericQueue<BatchReportQueueRequest>
      active={requests.find(({ statusId }) => statusId === Generating.id)}
      queued={{
        requests: requests.filter(({ statusId }) => statusId === Queued.id),
        onCancel: ({ id }) => cancel({ variables: { input: { id } } }),
      }}
      refetch={refetch}
      fetching={loading && networkStatus !== NetworkStatus.poll}
      details={({ progress }) => <BatchReportJobStatus progress={progress} />}
      options={({ reportIds, dateRange, payItems }) => [
        {
          label: 'Reports',
          children: (
            <CIMSTags
              tags={reportIds.map((id) => ({
                text: Report.fromIdOrFail(id).name,
              }))}
            />
          ),
        },
        {
          label: 'Pay Item(s)',
          hidden: !payItems.length,
          children: (
            <CIMSTags tags={payItems.map(({ label }) => ({ text: label }))} />
          ),
        },
        {
          label: 'Date Range',
          hidden: !dateRange,
          children: (
            <Space size="small" align="center">
              {dateFormatter.shortDatetime(dateRange?.from)}-
              {dateFormatter.shortDatetime(dateRange?.to)}
            </Space>
          ),
        },
      ]}
    />
  )
}

export default Queue
